import { useId, useState, type FormEvent, type KeyboardEvent } from 'react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../components/liks/LiksDialog/LiksDialog';
import { LiksTextFormField } from '../../components/liks/LiksTextFormField/LiksTextFormField';
import { useI18n } from '../../i18n/useI18n';
import { StartCrawlExpertPanel } from './StartCrawlExpertPanel';
import './StartCrawlModal.css';

export type StartCrawlModalProps = {
  crawlUrl: string;
  crawlMaxDepth: string;
  crawlMaxPages: string;
  crawlUseStartUrlAsPrefixFilter: boolean;
  crawlAllowSubdomains: boolean;
  crawlExcludedUrls: string[];
  onCrawlUrlChange: (value: string) => void;
  onCrawlMaxDepthChange: (value: string) => void;
  onCrawlMaxPagesChange: (value: string) => void;
  onCrawlMaxDepthBlur: () => void;
  onCrawlMaxPagesBlur: () => void;
  onCrawlUseStartUrlAsPrefixFilterChange: (value: boolean) => void;
  onCrawlAllowSubdomainsChange: (value: boolean) => void;
  onCrawlExcludedUrlsChange: (value: string[]) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
  isSubmitting?: boolean;
  preventInvalidNumericKey: (event: KeyboardEvent<HTMLInputElement>) => void;
};

export function StartCrawlModal({
  crawlUrl,
  crawlMaxDepth,
  crawlMaxPages,
  crawlUseStartUrlAsPrefixFilter,
  crawlAllowSubdomains,
  crawlExcludedUrls,
  onCrawlUrlChange,
  onCrawlMaxDepthChange,
  onCrawlMaxPagesChange,
  onCrawlMaxDepthBlur,
  onCrawlMaxPagesBlur,
  onCrawlUseStartUrlAsPrefixFilterChange,
  onCrawlAllowSubdomainsChange,
  onCrawlExcludedUrlsChange,
  onSubmit,
  onClose,
  isSubmitting = false,
  preventInvalidNumericKey,
}: StartCrawlModalProps) {
  const { translate } = useI18n();
  const prefixFilterSwitchId = useId();
  const allowSubdomainsSwitchId = useId();
  const expertPanelId = useId();
  const [isExpertOpen, setIsExpertOpen] = useState(false);

  const canSubmit = crawlUrl.trim() !== '';

  return (
    <LiksDialog
      isOpen
      as="form"
      size="md"
      title={translate('knowledge.startCrawlModalTitle')}
      closeLabel={translate('knowledge.form.close')}
      onClose={onClose}
      onSubmit={onSubmit}
      isDismissDisabled={isSubmitting}
      closeDisabled={isSubmitting}
      footer={
        <>
          <LiksButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {translate('knowledge.form.cancel')}
          </LiksButton>
          <LiksButton type="submit" disabled={isSubmitting || !canSubmit}>
            {translate('knowledge.startCrawl')}
          </LiksButton>
        </>
      }
    >
      <LiksTextFormField
        label={translate('knowledge.startCrawlUrlLabel')}
        type="url"
        value={crawlUrl}
        onChange={onCrawlUrlChange}
        placeholder={translate('knowledge.crawlUrlPlaceholder')}
        required
        autoFocus
      />

      <StartCrawlExpertPanel
        expertPanelId={expertPanelId}
        isExpertOpen={isExpertOpen}
        onToggleExpert={() => setIsExpertOpen((open) => !open)}
        crawlMaxDepth={crawlMaxDepth}
        crawlMaxPages={crawlMaxPages}
        crawlUseStartUrlAsPrefixFilter={crawlUseStartUrlAsPrefixFilter}
        crawlAllowSubdomains={crawlAllowSubdomains}
        crawlExcludedUrls={crawlExcludedUrls}
        prefixFilterSwitchId={prefixFilterSwitchId}
        allowSubdomainsSwitchId={allowSubdomainsSwitchId}
        isSubmitting={isSubmitting}
        onCrawlMaxDepthChange={onCrawlMaxDepthChange}
        onCrawlMaxPagesChange={onCrawlMaxPagesChange}
        onCrawlMaxDepthBlur={onCrawlMaxDepthBlur}
        onCrawlMaxPagesBlur={onCrawlMaxPagesBlur}
        onCrawlUseStartUrlAsPrefixFilterChange={
          onCrawlUseStartUrlAsPrefixFilterChange
        }
        onCrawlAllowSubdomainsChange={onCrawlAllowSubdomainsChange}
        onCrawlExcludedUrlsChange={onCrawlExcludedUrlsChange}
        preventInvalidNumericKey={preventInvalidNumericKey}
      />
    </LiksDialog>
  );
}
